/**
 * Report Upload Service
 * Handles the complete end-to-end blood-report upload flow:
 *   1. Validate file (type, size)
 *   2. Upload to Supabase Storage (private bucket `hormone-reports`)
 *   3. Create row in `uploaded_reports` table
 *   4. Call backend `/api/reports/extract-hormones` (GPT-4o vision OCR)
 *   5. Save extracted values back to `uploaded_reports` AND `hormone_reports`
 *   6. List / view / delete reports for the user
 */
import { supabase } from "./supabase";

const BUCKET = "hormone-reports";
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────
export const validateReportFile = (file) => {
  if (!file) return "No file selected";
  if (!ALLOWED_TYPES.includes(file.type)) {
    return `Unsupported file type. Please upload a JPG, PNG, or PDF (got: ${file.type || "unknown"})`;
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `File too large. Maximum size is 10 MB (got: ${(file.size / 1024 / 1024).toFixed(1)} MB)`;
  }
  return null; // valid
};

// ─────────────────────────────────────────────────────────────
// Upload to Supabase Storage
// ─────────────────────────────────────────────────────────────
const fileExtensionFor = (file) => {
  if (file.type === "application/pdf") return "pdf";
  if (file.type === "image/png") return "png";
  return "jpg";
};

export const uploadReportToStorage = async (userId, file) => {
  const ext = fileExtensionFor(file);
  const path = `${userId}/${Date.now()}-${crypto.randomUUID()}.${ext}`;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });

  if (error) {
    console.error("[ReportUpload] Storage upload failed:", error);
    const msg = (error.message || "").toLowerCase();
    if (msg.includes("bucket") && msg.includes("not found")) {
      throw new Error(
        "Report storage is not yet set up. Please contact support or run the database setup script."
      );
    }
    if (msg.includes("permission") || msg.includes("policy") || msg.includes("unauthorized")) {
      throw new Error("You don't have permission to upload reports. Please sign in again.");
    }
    if (msg.includes("size")) {
      throw new Error("File too large. Maximum size is 10 MB.");
    }
    throw new Error(error.message || "Upload failed. Please try again.");
  }
  return { path: data.path, fileType: file.type, fileName: file.name };
};

export const getReportSignedUrl = async (path) => {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) {
    console.error("[ReportUpload] Signed URL failed:", error);
    throw new Error(error.message || "Could not generate file URL");
  }
  return data.signedUrl;
};

// ─────────────────────────────────────────────────────────────
// uploaded_reports table operations
// ─────────────────────────────────────────────────────────────
export const insertUploadedReport = async ({ userId, fileName, filePath, fileType }) => {
  const { data, error } = await supabase
    .from("uploaded_reports")
    .insert({
      user_id: userId,
      file_name: fileName,
      file_path: filePath,
      file_type: fileType,
      upload_status: "success",
      analysis_status: "processing",
    })
    .select("id, created_at")
    .single();
  if (error) {
    console.error("[ReportUpload] DB insert failed:", error);
    throw new Error(error.message || "Could not save report record");
  }
  return data;
};

export const updateReportWithExtraction = async (reportId, payload) => {
  const { error } = await supabase
    .from("uploaded_reports")
    .update(payload)
    .eq("id", reportId);
  if (error) {
    console.error("[ReportUpload] DB update failed:", error);
    throw new Error(error.message || "Could not update report record");
  }
};

// ─────────────────────────────────────────────────────────────
// Backend extraction
// ─────────────────────────────────────────────────────────────
export const callBackendExtraction = async ({ reportId, imageUrl, fileType }) => {
  const resp = await fetch(`${BACKEND_URL}/api/reports/extract-hormones`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ report_id: reportId, image_url: imageUrl, file_type: fileType }),
  });
  if (!resp.ok) {
    throw new Error(`Extraction service returned ${resp.status}`);
  }
  return resp.json();
};

// ─────────────────────────────────────────────────────────────
// Save extracted values to hormone_reports (drives existing analysis engine)
// ─────────────────────────────────────────────────────────────
export const saveHormonesFromReport = async (userId, extracted) => {
  const row = {
    user_id: userId,
    lh: extracted.lh,
    fsh: extracted.fsh,
    insulin: extracted.insulin,
    testosterone: extracted.testosterone,
    amh: extracted.amh,
    report_date: new Date().toISOString().split("T")[0],
  };
  // Remove null values — the existing hormone_reports rows may have NOT NULL on some columns;
  // PostgREST will accept missing keys (column defaults to null in DB if nullable).
  Object.keys(row).forEach((k) => row[k] === null && delete row[k]);

  // Only insert if at least one hormone was extracted
  const hasAny = ["lh", "fsh", "insulin", "testosterone", "amh"].some(
    (k) => extracted[k] !== null && extracted[k] !== undefined
  );
  if (!hasAny) return null;

  const { data, error } = await supabase
    .from("hormone_reports")
    .insert(row)
    .select("id")
    .single();
  if (error) {
    console.error("[ReportUpload] hormone_reports insert failed:", error);
    return null; // non-fatal — uploaded_reports still has the data
  }
  return data;
};

// ─────────────────────────────────────────────────────────────
// COMPLETE PIPELINE: file → storage → DB → OCR → analysis
// ─────────────────────────────────────────────────────────────
/**
 * Run the full report-upload pipeline.
 * Throws on hard failures (validation, storage upload, DB insert).
 * Non-fatal failures (extraction service down) leave the report row
 * marked analysis_status='failed' so user can retry / enter manually.
 *
 * @returns { reportId, filePath, signedUrl, extraction }
 */
export const processReportUpload = async (userId, file, onProgress = () => {}) => {
  // 1. Validate
  const validationError = validateReportFile(file);
  if (validationError) throw new Error(validationError);

  // 2. Upload to storage
  onProgress("uploading");
  const stored = await uploadReportToStorage(userId, file);

  // 3. Create DB row
  onProgress("saving-record");
  const record = await insertUploadedReport({
    userId,
    fileName: stored.fileName,
    filePath: stored.path,
    fileType: stored.fileType,
  });

  // 4. Generate signed URL for backend to read
  const signedUrl = await getReportSignedUrl(stored.path);

  // 5. Call backend extraction (skip for PDF — backend returns 'unsupported')
  onProgress("extracting");
  let extractionResp;
  try {
    extractionResp = await callBackendExtraction({
      reportId: record.id,
      imageUrl: signedUrl,
      fileType: stored.fileType,
    });
  } catch (e) {
    // Mark report as failed but DON'T throw — file is uploaded successfully
    await updateReportWithExtraction(record.id, {
      analysis_status: "failed",
      error_message: e.message,
    });
    return {
      reportId: record.id,
      filePath: stored.path,
      signedUrl,
      extraction: null,
      error: `File uploaded but analysis failed: ${e.message}`,
    };
  }

  // 6. Persist extraction result back to uploaded_reports
  const e = extractionResp.extracted || {};
  await updateReportWithExtraction(record.id, {
    analysis_status: extractionResp.status, // 'completed' | 'failed'
    extracted_lh: e.lh ?? null,
    extracted_fsh: e.fsh ?? null,
    extracted_insulin: e.insulin ?? null,
    extracted_testosterone: e.testosterone ?? null,
    extracted_amh: e.amh ?? null,
    analysis_summary: extractionResp.summary || null,
    raw_extracted_text: e.raw_text || null,
    error_message: e.error || null,
  });

  // Note: We do NOT auto-save to hormone_reports here.
  // The user must explicitly confirm extracted values on the Review screen first.
  // Call `confirmAndSaveHormones(userId, extracted)` after user confirms.

  return {
    reportId: record.id,
    filePath: stored.path,
    signedUrl,
    extraction: extractionResp,
    error: null,
  };
};

/**
 * Called after the user reviews + confirms extracted hormone values
 * on the Review screen. Writes to `hormone_reports` so the existing
 * analysis engine + Care personalization pick them up.
 *
 * @param userId
 * @param values  { lh, fsh, insulin, testosterone, amh }  — confirmed/edited by user
 * @returns The saved hormone_reports row id, or null on failure.
 */
export const confirmAndSaveHormones = async (userId, values) => {
  return saveHormonesFromReport(userId, values);
};

/**
 * Retry OCR extraction on an existing uploaded report (file is already in storage).
 * Used by the Retry Analysis button in My Reports.
 * @param report  row from uploaded_reports (must include id, file_path, file_type)
 * @returns { extraction, error }
 */
export const retryExtraction = async (report) => {
  // Mark as processing
  await updateReportWithExtraction(report.id, {
    analysis_status: "processing",
    error_message: null,
  });

  let signedUrl;
  try {
    signedUrl = await getReportSignedUrl(report.file_path);
  } catch (e) {
    await updateReportWithExtraction(report.id, {
      analysis_status: "failed",
      error_message: `Could not access file: ${e.message}`,
    });
    return { extraction: null, error: e.message };
  }

  let resp;
  try {
    resp = await callBackendExtraction({
      reportId: report.id,
      imageUrl: signedUrl,
      fileType: report.file_type,
    });
  } catch (e) {
    await updateReportWithExtraction(report.id, {
      analysis_status: "failed",
      error_message: e.message,
    });
    return { extraction: null, error: e.message };
  }

  const e = resp.extracted || {};
  await updateReportWithExtraction(report.id, {
    analysis_status: resp.status,
    extracted_lh: e.lh ?? null,
    extracted_fsh: e.fsh ?? null,
    extracted_insulin: e.insulin ?? null,
    extracted_testosterone: e.testosterone ?? null,
    extracted_amh: e.amh ?? null,
    analysis_summary: resp.summary || null,
    raw_extracted_text: e.raw_text || null,
    error_message: e.error || null,
  });
  return { extraction: resp, error: null };
};

// ─────────────────────────────────────────────────────────────
// My Reports — list, view, delete
// ─────────────────────────────────────────────────────────────
export const fetchUserReports = async (userId) => {
  const { data, error } = await supabase
    .from("uploaded_reports")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[ReportUpload] fetchUserReports failed:", error);
    return [];
  }
  return data || [];
};

export const fetchReportWithUrl = async (reportId) => {
  const { data, error } = await supabase
    .from("uploaded_reports")
    .select("*")
    .eq("id", reportId)
    .single();
  if (error || !data) {
    console.error("[ReportUpload] fetchReportWithUrl failed:", error);
    return null;
  }
  try {
    data.signed_url = await getReportSignedUrl(data.file_path);
  } catch (e) {
    data.signed_url = null;
  }
  return data;
};

export const deleteReport = async (report) => {
  // Best-effort: remove storage object, then DB row.
  try {
    await supabase.storage.from(BUCKET).remove([report.file_path]);
  } catch (e) {
    console.warn("[ReportUpload] Storage delete failed (continuing):", e);
  }
  const { error } = await supabase.from("uploaded_reports").delete().eq("id", report.id);
  if (error) {
    console.error("[ReportUpload] DB delete failed:", error);
    throw new Error(error.message || "Could not delete report");
  }
};
